/**
 * PricingGuidancePanel - receipt-pricing decision input.
 *
 * Renders the claim's `pricing_guidance` block (with/without-receipt
 * reimbursement prices + whether THIS claim has a receipt) next to the
 * amount being judged - on the claim detail Decision area and inside the
 * capture dialog when the action's config asks for the "pricing" panel.
 * Purely presentational: labels, amounts, and receipt status all come
 * from the backend block; nothing is hardcoded.
 */

import { ReceiptText } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { CardDragHandle } from '@/shared/components/card-drag-handle'
import { useFormatting } from '@/admin/hooks/use-formatting'
import type { PricingGuidance } from '@/admin/hooks/use-claims'

export function PricingGuidancePanel({
  guidance,
  showEmptyNote = false,
}: {
  guidance: PricingGuidance | null | undefined
  /** Render a muted note instead of nothing when no pricing exists -
   * used in the capture dialog so the reviewer knows there is no
   * reference data for this product (vs. silently missing). */
  showEmptyNote?: boolean
}) {
  const { locale, currency: orgCurrency } = useFormatting()

  if (!guidance || guidance.prices.length === 0) {
    if (!showEmptyNote) return null
    return (
      <p className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-2.5 text-[12px] text-[var(--ink-3)]">
        No reference pricing on this product.
      </p>
    )
  }

  const formatPrice = (amount: number, currency: string | null) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || orgCurrency,
    }).format(amount)

  return (
    <div className="rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
          <CardDragHandle />
          <ReceiptText size={12} />
          Pricing guidance
        </span>
        {guidance.receipt && (
          <Badge variant={guidance.receipt.present ? 'default' : 'outline'}>
            {guidance.receipt.present
              ? `${guidance.receipt.label} attached`
              : `No ${guidance.receipt.label.toLowerCase()}`}
          </Badge>
        )}
      </div>
      <div className="mt-2.5 flex flex-col gap-1.5">
        {guidance.prices.map((price) => (
          <div
            key={price.code_name}
            className="flex items-baseline justify-between gap-4"
          >
            <span className="text-[12.5px] text-[var(--ink-2)]">
              {price.label}
            </span>
            <span className="font-mono text-[13px] text-[var(--ink)]">
              {formatPrice(price.amount, price.currency)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
